import { useCallback, useEffect } from 'react'
import * as yup from 'yup'
import { useQueryClient } from '@tanstack/react-query'
import { socket, SocketEvent } from '../sockets'
import { chatConversationsQueryKey } from '../api/chat'
import { MessageRow } from '../generated/api/chat-room'
import {
  insertMessageIntoCache,
  invalidateRoomReads,
  invalidateRoomSettingsQueries,
} from '../components/chat/Room/useRoomMessagesCache'
import { infiniteTicketQueries, ticketQueries } from '../api/ticket'
import { useAuth } from './useAuth'
import { notificationsQueries } from '../api/notifications'
import { featureQueries } from '../api/features'
import { societyQueries } from '../api/societies'
import {
  catchUpPublishedPosts,
  invalidatePublishedPost,
} from '../components/feed/postRowCache'

type TicketSocketAction =
  | 'new-ticket'
  | 'new-ticket-from-board'
  | 'new-ticket-message'
  | 'new-ticket-message-from-board'
  | 'ticket-status'
  | 'ticket-assigned'
  | 'ticket-variant'

const validTicketActions: TicketSocketAction[] = [
  'new-ticket',
  'new-ticket-from-board',
  'new-ticket-message',
  'new-ticket-message-from-board',
  'ticket-status',
  'ticket-assigned',
  'ticket-variant',
]

export const ticketPayloadSchema = yup.object({
  societyId: yup.string().required(),
  ticketId: yup.string().optional(),
  action: yup.mixed().oneOf(validTicketActions),
})

const getTicketUpdatedPayload = (
  payload: unknown
): yup.InferType<typeof ticketPayloadSchema> | false => {
  try {
    const ticketPayload = ticketPayloadSchema.validateSync(payload)
    return ticketPayload
  } catch (err) {
    return false
  }
}

const parsePayload = <T>(payload: T | string): T =>
  typeof payload === 'string' ? (JSON.parse(payload) as T) : payload

const useWebsocket = (): void => {
  const client = useQueryClient()
  const { token, userId } = useAuth()

  const setWebsocket = useCallback(
    async (_token: string): Promise<void> => {
      let hasConnected = false
      // Initiate socket
      await socket.setSocket(_token, [
        {
          event: SocketEvent.CONNECT,
          func: (): void => {
            // Only a reconnect can have missed events; the first connect
            // happens while the initial queries are already in flight.
            if (hasConnected) {
              client.invalidateQueries({ queryKey: [{ scope: 'chat' }] })
              catchUpPublishedPosts(client)
            }
            hasConnected = true
          },
        },
        {
          event: SocketEvent.MESSAGE_CREATED,
          func: (payload: MessageRow | string): void => {
            const row = parsePayload(payload)
            insertMessageIntoCache(client, row)
            invalidateRoomReads(client, row.roomId)
            client.invalidateQueries({ queryKey: chatConversationsQueryKey })
          },
        },
        {
          event: SocketEvent.RECEIPT_MOVED,
          func: (payload: { roomId: string } | string): void => {
            const { roomId } = parsePayload(payload)
            invalidateRoomReads(client, roomId)
          },
        },
        {
          event: SocketEvent.ROOM_UPDATED,
          func: (payload: { roomId: string } | string): void => {
            const { roomId } = parsePayload(payload)
            invalidateRoomSettingsQueries(client, roomId)
          },
        },
        {
          // Legacy event, still emitted by the server; the room pane relies on
          // chat:message-created instead.
          event: SocketEvent.RECEIVE_MESSAGE,
          func: (): void => {
            client.invalidateQueries({
              queryKey:
                notificationsQueries.getUserUnseenUnreadCount(userId).queryKey,
            })
          },
        },
        {
          event: SocketEvent.ROOMS_NEW,
          func: (): void => {
            client.invalidateQueries({ queryKey: chatConversationsQueryKey })
            client.invalidateQueries({
              queryKey:
                notificationsQueries.getUserUnseenUnreadCount(userId).queryKey,
            })
          },
        },
        {
          event: SocketEvent.NOTIFICATION_COUNTS,
          func: (): void => {
            client.invalidateQueries()
          },
        },
        {
          event: SocketEvent.FEATURES_UPDATED,
          func: (): void => {
            client.invalidateQueries({
              queryKey: featureQueries.getFeatures().queryKey,
            })
          },
        },
        {
          event: SocketEvent.SOCIETY_SETTINGS_UPDATED,
          func: (payload: { societyId: string } | string): void => {
            const { societyId } = parsePayload(payload)
            client.invalidateQueries({
              queryKey: societyQueries.getUserSocieties(userId).queryKey,
            })
            client.invalidateQueries({
              queryKey: societyQueries.getSociety(societyId).queryKey,
            })
            client.invalidateQueries({ queryKey: [{ scope: 'chat' }] })
          },
        },
        {
          event: SocketEvent.POST_PUBLISHED,
          func: (
            payload: { societyId: string; postId: string } | string
          ): void => {
            const { postId } = parsePayload(payload)
            invalidatePublishedPost(client, postId)
          },
        },
        {
          event: SocketEvent.POST_UPDATED,
          func: (
            payload: { societyId: string; postId: string } | string
          ): void => {
            const { postId } = parsePayload(payload)
            invalidatePublishedPost(client, postId)
          },
        },
        {
          event: SocketEvent.TICKET_UPDATED,
          func: (ticketPayload: unknown): void => {
            const parsedTicketPayload =
              typeof ticketPayload === 'string'
                ? JSON.parse(ticketPayload)
                : ticketPayload
            const payload = getTicketUpdatedPayload(parsedTicketPayload)

            if (!payload) {
              return
            }

            const { societyId, ticketId } = payload

            client.invalidateQueries({
              queryKey: infiniteTicketQueries.tickets({ societyId }).queryKey,
            })
            client.invalidateQueries({
              queryKey: infiniteTicketQueries.ticketsBoard({ societyId })
                .queryKey,
            })
            client.invalidateQueries({
              queryKey: [{ scope: 'ticket', entity: 'getUnreadTicketsCount' }],
            })

            if (ticketId) {
              client.invalidateQueries({
                queryKey: ticketQueries.getTicket(ticketId, societyId).queryKey,
              })
              client.invalidateQueries({
                queryKey: ticketQueries.getTicketAsBoard(ticketId, societyId)
                  .queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMessages({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMedia({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMessagesAsBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketMediaBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketDocuments({
                  societyId,
                  ticketId,
                }).queryKey,
              })
              client.invalidateQueries({
                queryKey: infiniteTicketQueries.getTicketDocumentsBoard({
                  societyId,
                  ticketId,
                }).queryKey,
              })
            }
          },
        },
      ])
    },
    [client, userId]
  )

  useEffect(() => {
    if (token) {
      setWebsocket(token)
    }
  }, [token, setWebsocket])
}

useWebsocket.displayName = 'useWebsocket'
export { useWebsocket }
